import React, { useCallback, useEffect, useMemo, useState, Fragment } from 'react';
import {ReactFlow ,addEdge ,Background ,Controls ,MiniMap ,useEdgesState ,useNodesState ,MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { BsFloppy } from 'react-icons/bs';
import { ArrowsRightLeftIcon, StarIcon, BellIcon, ArrowPathIcon, CalendarDaysIcon, ChartPieIcon, ChatBubbleOvalLeftIcon, CheckCircleIcon, ClockIcon, Cog6ToothIcon, ComputerDesktopIcon, DevicePhoneMobileIcon, EnvelopeIcon, GlobeAltIcon, HandRaisedIcon, LinkIcon, PhoneXMarkIcon, PowerIcon, UserIcon, RocketLaunchIcon, PencilIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { Transition, Dialog } from '@headlessui/react';
import { useDispatch } from 'react-redux';
import { createJourney, saveJourneyData, fetchJourneyDetails } from '../../redux/actions/journeyAction';
import { useNavigate, useParams } from 'react-router-dom';

// Import custom node components
import { SuccessEdge, FailureEdge } from '../journeyComponent/edges/CustomEdges';
import ForSpecificUsersNode from '../journeyComponent/nodes/ForSpecificUsersNode';
import SegmentNode from '../journeyComponent/nodes/SegmentNode';
import { ChevronDown, ChevronUp, HourglassBottom, Whatsapp } from 'react-bootstrap-icons';
import IsInSegmentNode from '../journeyComponent/nodes/IsInSegmentNode';
import HasDoneEventNode from '../journeyComponent/nodes/HasDoneEventNode';
import CheckUserAttributeNode from '../journeyComponent/nodes/CheckUserAttributeNode';
import IsUserReachableNode from '../journeyComponent/nodes/IsUserReachableNode';
import OccurrenceNode from '../journeyComponent/nodes/OccurrenceNode';
import UserAttributeNode from '../journeyComponent/nodes/UserAttributeNode';
import SendEmailNode from '../journeyComponent/nodes/SendEmailNode';
import SendSMSNode from '../journeyComponent/nodes/SendSMSNode';
import ShowInAppNode from '../journeyComponent/nodes/ShowInAppNode';
import CallAPINode from '../journeyComponent/nodes/CallAPINode';
import EndJourneyNode from '../journeyComponent/nodes/EndJourneyNode';
import SendPushNode from '../journeyComponent/nodes/SendPushNode';
import ShowOnSiteNotificationNode from '../journeyComponent/nodes/ShowOnSiteNotificationNode';
import SendWebPushNode from '../journeyComponent/nodes/SendWebPushNode';
import SendWhatsAppNode from '../journeyComponent/nodes/SendWhatsAppNode';
import SetUserAttributeNode from '../journeyComponent/nodes/SetUserAttributeNode';
import WaitForSomeTimeNode from '../journeyComponent/nodes/WaitForSomeTimeNode';
import WaitForTimeSlotsNode from '../journeyComponent/nodes/WaitForTimeSlotsNode';
import WaitForEventNode from '../journeyComponent/nodes/WaitForEventNode';
import WaitForDateNode from '../journeyComponent/nodes/WaitForDateNode';
import EmailDrawer from './EmailDrawer';
import WhatsappDrawer from './WhatsappDrawer';
import Toast from '../assetLibComponents/shared/Toast';
import { toast } from 'react-toastify';
import DateDrawer from './drawer/DateDrawer';
import WaitTimeDrawer from './drawer/WaitTimeDrawer';
import WaitTimeSlotsDrawer from './drawer/WaitTimeSlotsDrawer';
import StarNode from './nodes/StarNode';
// Add mock segments data
const segments = [
  "All Users",
  "Active Users",
  "Inactive Users",
  "New Users",
  "Premium Users",
  "Free Users",
  "High Value Customers",
  "At Risk Users",
  "Churned Users",
  "Beta Users"
];

// Add this near the top of your file, after the imports
const scrollbarStyles = `
  /* Custom Scrollbar Styles */
  .listing::-webkit-scrollbar-track {
    background: #f8f8f8;
    -webkit-box-shadow: 0px 0px 0px #f8f8f8;
    border: none;
  }
  .listing::-webkit-scrollbar-thumb {
    width:1px !important;
    background: #59B3C6;
  }
  .listing::-webkit-scrollbar-thumb {
    border-radius: 10px;
    scrollbar-width: thin;
  }
  .listing::-webkit-scrollbar {
    width: 4px;
  }

  .react-flow__handle{
    width: 10px;
    height: 10px;
  }

`;

// Add this near the top of your file, after the imports
const edgeOptions = {
  style: { strokeWidth: 2 },
  type: 'smoothstep',
  markerEnd: {
    type: MarkerType.ArrowClosed,
    width: 20,
    height: 20,
    color: '#b1b1b7',
  },
};

// Modify the CollapsibleSection component for styling
const CollapsibleSection = ({ title, items, color }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="mb-2">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="w-full text-left p-4 py-3 text-gray-800 bg-gray-300 rounded-lg flex justify-between items-center"
      >
        <span>{title}</span>
        <span>{isOpen ? <ChevronUp/> : <ChevronDown/>}</span>
      </button>
      <div 
        className={`mt-2 grid transition-all duration-700 ease-in-out grid-cols-3 gap-4 ${isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'}`}
      >
        {items.map((item) => (
          <>
            { title !== "Stakeholders" ?
              <div 
                key={item.label} 
                className="flex flex-col items-center"
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/reactflow', item.type);
                  event.dataTransfer.setData('application/source', item.source);
                }}
              >
                <div className={`w-12 h-12 rounded-full ${color} flex items-center justify-center`}>
                  <item.icon className="w-6 h-6" />
                </div>
                <span className="text-gray-800 text-[10px] text-center mt-1">{item.label}</span>
              </div> 
            :
              <div 
                key={item.label} 
                className="flex flex-col items-center"
                draggable
                onDragStart={(event) => {
                  event.dataTransfer.setData('application/reactflow', item.type);
                  event.dataTransfer.setData('application/source', item.source);
                }}
              >
                <div className={`w-16 h-16 ${item.color} border-2 p-2 ${item.border} rounded-full flex items-center justify-center`}>
                  <span className="text-gray-800 font-bold text-[10px] leading-1 text-center mt-1">{item.label}</span>
                </div>
              </div> 
            }
          </>
        ))}
      </div>
    </div>
  );
};

// Define the node types and their corresponding labels and icons
const nodeTypesData = [
  {
    title: "Triggers",
    items: [
      { label: 'Occurrence of Event', icon: StarIcon, type: 'occurrence' },
      { label: 'Enter/Exit/Is In Segment', icon: ArrowsRightLeftIcon, type: 'segment' },
      { label: 'Change in User Attribute', icon: Cog6ToothIcon, type: 'userAttribute' },
      { label: 'For Specific Users', icon: UserIcon, type: 'specificUsers' },
    ],
    color: "bg-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]",
  },
  {
    title: "Actions",
    items: [
      { label: 'Send Email', icon: EnvelopeIcon, type: 'sendEmail', source: 'actions' },
      { label: 'Send WhatsApp', icon: Whatsapp, type: 'sendWhatsApp', source: 'actions' },
      { label: 'Send Push', icon: BellIcon, type: 'sendPush', source: 'actions' },
      { label: 'Show In-App', icon: DevicePhoneMobileIcon, type: 'showInApp' },
      { label: 'Show On-Site Notification', icon: ComputerDesktopIcon, type: 'showOnSiteNotification' },
      { label: 'Send Web Push', icon: GlobeAltIcon, type: 'sendWebPush' },
      { label: 'Send SMS', icon: ChatBubbleOvalLeftIcon, type: 'sendSMS' },
      { label: 'Call API', icon: LinkIcon, type: 'callAPI' },
      { label: 'Set User Attribute', icon: Cog6ToothIcon, type: 'setUserAttribute' },
    ],
    color: "bg-[#19945980]",
  },
  {
    title: "Conditions",
    items: [
      { label: 'Is in Segment', icon: ChartPieIcon, type: 'isInSegment' },
      { label: 'Has Done Event', icon: CheckCircleIcon, type: 'hasDoneEvent' },
      { label: 'Check User Attribute', icon: Cog6ToothIcon, type: 'checkuserAttribute' },
      { label: 'Is User Reachable', icon: PhoneXMarkIcon, type: 'isUserReachable' },
    ],
    color: "bg-[#F6971A80]",
  },
  {
    title: "Flow Controls",
    items: [
      { label: 'Wait for Some Time', icon: HourglassBottom, type: 'waitForSomeTime' },
      { label: 'Wait for Time Slots', icon: ClockIcon, type: 'waitForTimeSlots' },
      // { label: 'Wait for Event', icon: BellIcon, type: 'waitForEvent' },
      { label: 'Wait for Date', icon: CalendarDaysIcon, type: 'waitForDate' },
      { label: 'End Journey', icon: PowerIcon, type: 'endJourney' },
    ],
    color: "bg-[#D6201C80]",
  },
  {
    title: "Stakeholders",
    items: [
      { label: 'MFD', icon: Whatsapp, type: 'mfd', source: 'stakeholders', color: "bg-[#F6971A80]", border:"border-yellow-200" },
      { label: 'Sales Manager', icon: EnvelopeIcon, type: 'manager', source: 'stakeholders', color: "bg-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]", border:"border-blue-200" },
      { label: 'Sales Head', icon: BellIcon, type: 'saleshead', source: 'stakeholders', color: "bg-[#19945980]", border:"border-green-200" },
      { label: 'Branch Head', icon: BellIcon, type: 'branchhead', source: 'stakeholders', color: "bg-[#D6201C80]", border:"border-red-200" },
    ],
    color: "bg-[#19945980]",
  }
];

// Add this near the nodeTypesData definition
const defaultNodeEdges = {
  segment: 1,
  occurrence: 1,
  isInSegment: 2,
  hasDoneEvent: 2,
  isUserReachable: 2,
  checkuserAttribute: 2,
  specificUsers: 1,
  userAttribute: 1,
  sendEmail: 5,
  sendSMS: 4,
  showInApp: 2,
  callAPI: 4,
  endJourney: 1,
  sendPush: 4,
  showOnSiteNotification: 4,
  sendWebPush: 4,
  sendWhatsApp: 5,
  setUserAttribute: 4,
  waitForSomeTime: 1,
  waitForTimeSlots: 1,
  waitForEvent: 1,
  waitForDate: 1
};

const JourneyBuilder = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get journey ID from URL params
  const { id } = useParams(); 
  
  // Add state to track node edge counts
  const [nodeEdgeCounts, setNodeEdgeCounts] = useState({});
  
  // Initial nodes and edges
  const initialNodes = []; // Remove the start node
  const initialEdges = []; // Start with no edges

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  
  // Add new state for journey dialog
  const [showJourneyDialog, setShowJourneyDialog] = useState(false);

  // Add these states in your JourneyBuilder component
  const [isEmailDrawerOpen, setIsEmailDrawerOpen] = useState(false);
  const [selectedEmailNode, setSelectedEmailNode] = useState(null);
  
  const [journeyName, setJourneyName] = useState('');
  const [selectedSegment, setSelectedSegment] = useState('');
  const [loaderSaveJourney, setLoaderSaveJourney] = useState(false);
  
  // Add new state for date drawer
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const [selectedDateNode, setSelectedDateNode] = useState(null);
  
  // Add new state for wait time drawer
  const [isWaitTimeDrawerOpen, setIsWaitTimeDrawerOpen] = useState(false);
  const [selectedWaitTimeNode, setSelectedWaitTimeNode] = useState(null);
  
  // Add these new states in the JourneyBuilder component
  const [isWaitTimeSlotsDrawerOpen, setIsWaitTimeSlotsDrawerOpen] = useState(false);
  const [selectedWaitTimeSlotsNode, setSelectedWaitTimeSlotsNode] = useState(null);
  const [isWhatsappDrawerOpen, setIsWhatsappDrawerOpen] = useState(null);
  const [selectedWhatsappNode, setSelectedWhatsappNode] = useState(null);
  
  // Add new state for segment statistics
  const [segmentStats, setSegmentStats] = useState({
    totalUsers: 525711,
    knownUsers: 124994,
    unknownUsers: 400718,
    reachableUsers: 348513
  });
  
  useEffect(() => {
    props.pageTypeHandler("Journey");
    
    // Fetch journey details if ID exists
    if (id) {
      dispatch(fetchJourneyDetails(props.authToken, id, (journeyData) => {
        // Handle successful fetch
        if (journeyData) {
          setJourneyName(journeyData.title || '');
          setSelectedSegment(journeyData.segment || '');
          
          // Update nodes with necessary handlers
          const nodesWithHandlers = journeyData.nodes.map(node => ({
            ...node,
            data: {
              ...node.data,
              onEdgeCountChange: (newCount) => handleEdgeCountChange(node.id, newCount),
              onDelete: handleNodeDelete,
              onEdgeDelete: handleEdgeDelete,
              outputEdges: defaultNodeEdges[node.type] || 1,
            }
          }));

          // Initialize node edge counts
          const initialEdgeCounts = {};
          nodesWithHandlers.forEach(node => {
            initialEdgeCounts[node.id] = defaultNodeEdges[node.type] || 1;
          });
          setNodeEdgeCounts(initialEdgeCounts);

          // Set nodes and edges
          setNodes(nodesWithHandlers);
          setEdges(journeyData.edges || []);
        }
      }));
    } else {
      // If no ID, show the journey creation dialog
      navigate('/journey');
    }
  }, [id]);

  const onConnect = useCallback(
    (params) => {
      // Get the source node and output handle ID
      const sourceNode = nodes.find(node => node.id === params.source);
      const outputIndex = parseInt(params.sourceHandle?.split('-')[1] || 0);
      
      // Get the label based on node type
      let label = '';
      if (sourceNode?.type === 'sendPush') {
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Click', 'Ignore', 'Interact', 'Block'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'sendWhatsApp') {
        // Check the source type for WhatsApp nodes
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Send', 'Delivery', 'Failure', 'Read', 'Click'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'sendEmail') {
        if (sourceNode.data.source === 'seller' || sourceNode.data.source === 'manager') {
          label = 'Notify';
        } else {
          const edgeLabels = ['Send', 'Open', 'Click', 'Unsubscribe', 'Bounce'];
          label = edgeLabels[outputIndex];
        }
      } else if (sourceNode?.type === 'isInSegment' || sourceNode?.type === 'hasDoneEvent' || sourceNode?.type === 'checkuserAttribute' || sourceNode?.type === 'isUserReachable') {
        const edgeLabels = ['Yes', 'No'];
        label = edgeLabels[outputIndex];
      }

      // Add other node types here if needed

      const edge = {
        ...params,
        ...edgeOptions,  // Add default edge options
        label: label,
        labelStyle: { fill: '#888', fontSize: 12 },
        labelBgStyle: { fill: 'white' },
        labelBgPadding: [8, 4],
        labelBgBorderRadius: 4
      };

      setEdges((eds) => addEdge(edge, eds));
    },
    [nodes, setEdges]
  );

  const onDragStart = (event, nodeType) => {
    event.dataTransfer.setData('application/reactflow', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  // Add handler for updating edge counts
  const handleEdgeCountChange = useCallback((nodeId, newCount) => {
    setNodeEdgeCounts(prev => ({
      ...prev,
      [nodeId]: newCount
    }));
  }, []);

  // Add these handlers for delete operations
  const handleNodeDelete = useCallback((nodeId) => {
    setNodes((nodes) => nodes.filter((node) => node.id !== nodeId));
    // Also remove any connected edges
    setEdges((edges) => edges.filter((edge) => 
      edge.source !== nodeId && edge.target !== nodeId
    ));
  }, [setNodes, setEdges]);

  const handleEdgeDelete = useCallback((nodeId, handleId) => {
    setEdges((edges) => edges.filter((edge) => 
      !(edge.source === nodeId && edge.sourceHandle === handleId)
    ));
  }, [setEdges]);

  // Modify the onDrop callback to include delete handlers
  const onDrop = useCallback(
    (event) => {
      event.preventDefault();

      const type = event.dataTransfer.getData('application/reactflow');
      const source = event.dataTransfer.getData('application/source');
      
      const position = {
        x: event.clientX - event.target.getBoundingClientRect().left,
        y: event.clientY - event.target.getBoundingClientRect().top,
      };

      const newNodeId = `${Date.now()}`;
      
      // Create the new node
      const newNode = {
        id: newNodeId,
        // For stakeholder nodes, use the type directly
        type: source === 'stakeholders' ? type : type,
        position,
        data: { 
          label: type === 'mfd' ? 'MFD' :
                 type === 'manager' ? 'Sales Manager' :
                 type === 'saleshead' ? 'Sales Head' :
                 type === 'branchhead' ? 'Branch Head' : type,
          type: type, // Add the type to data
          source: source,
          onEdgeCountChange: (newCount) => handleEdgeCountChange(newNodeId, newCount),
          outputEdges: defaultNodeEdges[type],
          onDelete: handleNodeDelete,
          onEdgeDelete: handleEdgeDelete
        },
      };

      setNodes((nds) => nds.concat(newNode));

      // Open email drawer if the node type is sendEmail
      if (type === 'sendEmail') {
        setSelectedEmailNode(newNode);
        // setIsEmailDrawerOpen(true);
      }
      if (type === 'sendWhatsApp') {
        setSelectedWhatsappNode(newNode);
        // setIsWhatsappDrawerOpen(true);
      }
    },
    [setNodes, handleEdgeCountChange, handleNodeDelete, handleEdgeDelete]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  // Memoize nodeTypes
  const nodeTypes = useMemo(() => ({
    segment: (props) => (
      <SegmentNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.segment}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    occurrence: (props) => (
      <OccurrenceNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.occurrence}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    isInSegment: (props) => (
      <IsInSegmentNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.isInSegment}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    hasDoneEvent: (props) => (
      <HasDoneEventNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.hasDoneEvent}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    isUserReachable: (props) => (
      <IsUserReachableNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.isUserReachable}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    checkuserAttribute: (props) => (
      <CheckUserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.checkuserAttribute}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    specificUsers: (props) => (
      <ForSpecificUsersNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.specificUsers}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    userAttribute: (props) => (
      <UserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.userAttribute}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendEmail: (props) => (
      <SendEmailNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendEmail}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendSMS: (props) => (
      <SendSMSNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendSMS}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    showInApp: (props) => (
      <ShowInAppNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.showInApp}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    callAPI: (props) => (
      <CallAPINode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.callAPI}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    endJourney: (props) => (
      <EndJourneyNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.endJourney}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendPush: (props) => (
      <SendPushNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendPush}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    showOnSiteNotification: (props) => (
      <ShowOnSiteNotificationNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.showOnSiteNotification}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendWebPush: (props) => (
      <SendWebPushNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendWebPush}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    sendWhatsApp: (props) => (
      <SendWhatsAppNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.sendWhatsApp}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    setUserAttribute: (props) => (
      <SetUserAttributeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.setUserAttribute}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForSomeTime: (props) => (
      <WaitForSomeTimeNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.waitForSomeTime}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForTimeSlots: (props) => (
      <WaitForTimeSlotsNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.waitForTimeSlots}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForEvent: (props) => (
      <WaitForEventNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.waitForEvent}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    waitForDate: (props) => (
      <WaitForDateNode 
        {...props}
        outputEdges={nodeEdgeCounts[props.id] || defaultNodeEdges.waitForDate}
        onEdgeCountChange={(newCount) => handleEdgeCountChange(props.id, newCount)}
      />
    ),
    mfd: StarNode,
    manager: StarNode,
    saleshead: StarNode,
    branchhead: StarNode,
  }), [nodeEdgeCounts, handleEdgeCountChange]); 

  // Memoize edgeTypes
  const edgeTypes = useMemo(() => ({
    success: SuccessEdge,
    failure: FailureEdge,
  }), []); 

  // Function to format numbers with commas
  const formatNumber = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  };

  // Add these handlers
  const handleEmailDrawerClose = () => {
    setIsEmailDrawerOpen(false);
    setSelectedEmailNode(null);
  };
  // Add these handlers
  const handleWhatsappDrawerClose = () => {
    setIsWhatsappDrawerOpen(false);
    setSelectedWhatsappNode(null);
  };

  const handleWhatsappDrawerSave = (whatsappData) => {
    if (selectedWhatsappNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedWhatsappNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  whatsappConfig: whatsappData,
                  label: whatsappData.campaignName || 'Send Whatsapp', // Add the campaign name as the node label
                },
              }
            : node
        )
      );
    }
    handleWhatsappDrawerClose();
  };
  const handleEmailDrawerSave = (emailData) => {
    if (selectedEmailNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedEmailNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  emailConfig: emailData,
                  label: emailData.campaignName || 'Send Email', // Add the campaign name as the node label
                },
              }
            : node
        )
      );
    }
    handleEmailDrawerClose();
  };

  // Add this new function to handle journey save
  const handleSaveJourney = () => {
    const journeyData = {
      nodes: nodes.map(node => ({
        id: node.id,
        type: node.type,
        position: node.position,
        data: {
          ...node.data,
          label: node.data.label || node.type,
          emailConfig: node.data.emailConfig, // Include email configurations
        },
      })),
      edges: edges,
      metadata: {
        createdAt: new Date().toISOString(),
        version: "1.0"
      }
    };

    // Log the journey data
    setLoaderSaveJourney(true);
    console.log('Journey Data:', journeyData);
    dispatch(saveJourneyData(props.authToken, id, journeyData, onSuccessSave));
  };

  // Add this new function
  const handleUpdateJourney = () => {
    // Validate inputs
    if (!journeyName.trim()) {
      toast.error(<Toast message="Please enter a journey name" />);
      return;
    }

    if (!selectedSegment) {
      toast.error(<Toast message="Please select a segment" />);
      return;
    }

    // Call the API through Redux action
    dispatch(createJourney(props.authToken, id, journeyName, selectedSegment, onSuccessUpdate));
  };
  
  const onSuccessSave = () => {
    setTimeout(() => {
      setLoaderSaveJourney(false);
    }, 1000);
  }
  
  const onSuccessUpdate = () => {
    setShowJourneyDialog(false);
  }

  // Add handler for date selection
  const handleDateSelection = (date) => {
    if (selectedDateNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedDateNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  selectedDate: date,
                },
              }
            : node
        )
      );
    }
    setIsDateDrawerOpen(false);
    setSelectedDateNode(null);
  };

  // Add handler for wait time selection
  const handleWaitTimeSelection = (waitTimeData) => {
    if (selectedWaitTimeNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedWaitTimeNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  waitTimeConfig: waitTimeData,
                  label: `Wait for ${waitTimeData.value} ${waitTimeData.unit}`, // Update node label
                },
              }
            : node
        )
      );
    }
    setIsWaitTimeDrawerOpen(false);
    setSelectedWaitTimeNode(null);
  };

  // Update the onNodeClick handler to include the new node type
  const onNodeClick = useCallback((event, node) => {
    if (node.type === 'sendEmail') {
      setSelectedEmailNode(node);
      setIsEmailDrawerOpen(true);
    } else if (node.type === 'waitForDate') {
      setSelectedDateNode(node);
      setIsDateDrawerOpen(true);
    } else if (node.type === 'waitForSomeTime') {
      setSelectedWaitTimeNode(node);
      setIsWaitTimeDrawerOpen(true);
    } else if (node.type === 'waitForTimeSlots') {
      setSelectedWaitTimeSlotsNode(node);
      setIsWaitTimeSlotsDrawerOpen(true);
    }
  }, []);

  // Add handler for time slots selection
  const handleWaitTimeSlotsSelection = (timeSlotsData) => {
    if (selectedWaitTimeSlotsNode) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedWaitTimeSlotsNode.id
            ? {
                ...node,
                data: {
                  ...node.data,
                  timeSlotConfig: timeSlotsData,
                  label: `Wait for Time Slots: ${timeSlotsData.displayText}`,
                },
              }
            : node
        )
      );
    }
    setIsWaitTimeSlotsDrawerOpen(false);
    setSelectedWaitTimeSlotsNode(null);
  };

  return (
    <>
      {/* Add the styles to the DOM */}
      <style>{scrollbarStyles}</style>
      
      {/* Add the floating save button */}
      <div className="fixed top-20 right-12 z-10 flex flex-col gap-2">
        <button
          onClick={handleSaveJourney}
          disabled={loaderSaveJourney}
          className={`flex items-center justify-center w-[90px] cursor-pointer gap-2 px-4 py-2 bg-[var(--brand-primary)] text-white rounded-full shadow-lg transition-colors duration-200 ${
            loaderSaveJourney ? 'opacity-75' : 'hover:bg-[#3599ab]'
          }`}
        >
          {loaderSaveJourney ? (
            <>
              <ArrowPathIcon className="w-5 h-5 animate-spin" />
            </>
          ) : (
            <>
              <BsFloppy className="w-4 h-4" />
              Save
            </>
          )}
        </button>
      </div>

      <div className="flex h-screen w-full pt-16">
        {/* Left Sidebar - 20% */}
        <div className="w-[20%] flex-shrink-0 border-r border-gray-200 bg-white overflow-hidden flex flex-col">
          {/* Tab Content - Add custom-scrollbar class */}
          <div className='font-bold py-3 pl-5 pr-2 bg-[#738689] text-xl text-white flex justify-between items-center'>
            <div title={journeyName} className='truncate max-w-[85%]'>
              {journeyName}
            </div>
            <PencilSquareIcon 
              className='w-5 h-5 cursor-pointer hover:text-gray-200 transition-colors' 
              onClick={() => {setShowJourneyDialog(true)}}
            />
          </div>
          <div className="flex-1 overflow-y-auto p-2 listing bg-gray-100">
            {nodeTypesData.map((section, index) => (
              <CollapsibleSection 
                key={index}
                title={section.title} 
                items={section.items.map(item => ({
                  ...item,
                  // Add onDragStart to enable dragging
                  onDragStart: (event) => onDragStart(event, item.type),
                }))} 
                color={section.color} 
              />
            ))}
          </div>
        </div>

        {/* Flow Canvas - dynamic width based on preview state */}
        <div className={`w-[79%] flex-shrink-0 h-full transition-all duration-300`}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onNodeClick={onNodeClick}
            fitView
            className="bg-gray-50"
          >
            <Controls/>
            <Background />
            <MiniMap pannable="true" zoomable="true" />
          </ReactFlow>
        </div>
      </div>

      <Transition.Root show={showJourneyDialog} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => setShowJourneyDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 backdrop-blur-sm transition-opacity" />
          </Transition.Child>
          <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-3xl">
                    <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                      <div className="sm:flex sm:items-start">
                        <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                          <Dialog.Title as="h3" className="text-xl font-semibold border-b border-gray-200 pb-2 leading-6 text-gray-900 mb-4">
                            Edit Journey 
                          </Dialog.Title>
                          <div className="mt-2 space-y-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-500 mb-1">
                                Journey Name
                              </label>
                              <input
                                type="text"
                                value={journeyName}
                                onChange={(e) => setJourneyName(e.target.value)}
                                className="block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[var(--brand-primary)] sm:text-sm sm:leading-6"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-500 mb-1">
                                Segment
                              </label>
                              <div className="flex gap-2 items-center">
                              <select
                                  value={selectedSegment}
                                  onChange={(e) => setSelectedSegment(e.target.value)}
                                  className="block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-[var(--brand-primary)] sm:text-sm sm:leading-6"
                                >
                                  <option value="" disabled>Select Segment</option>
                                  {segments.map((segment) => (
                                    <option key={segment} value={segment}>{segment}</option>
                                  ))}
                                </select>
                                <span className="text-gray-500 mx-2">or</span>
                                <button
                                  type="button"
                                  className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-[var(--brand-primary)] border border-[var(--brand-primary)] shadow-sm hover:bg-gray-50"
                                >
                                  Create New Segment
                                </button>
                              </div>
                            </div>

                            {/* Segment Statistics Box */}
                            {selectedSegment?.length > 0 &&<div className="mt-4 border border-gray-200 rounded-lg p-4">
                              <div className="grid grid-cols-4 gap-4">
                                <div>
                                  <div className="text-sm text-gray-500">Total Users:</div>
                                  <div className="text-lg font-semibold">{formatNumber(segmentStats.totalUsers)}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500">Known Users:</div>
                                  <div className="text-lg font-semibold">{formatNumber(segmentStats.knownUsers)}</div>
                                </div>
                                <div>
                                  <div className="text-sm text-gray-500">Unknown Users:</div>
                                  <div className="text-lg font-semibold">{formatNumber(segmentStats.unknownUsers)}</div>
                                </div>
                                <div className="relative">
                                  <div className="text-sm text-gray-500 flex items-center">
                                    Reachable Users:
                                    <span className="ml-1 group">
                                      <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                      </svg>
                                      <span className="hidden group-hover:block absolute z-10 w-48 p-2 mt-1 text-sm text-gray-500 bg-white border border-gray-200 rounded-lg shadow-lg">
                                        Users who can be reached through at least one channel
                                      </span>
                                    </span>
                                  </div>
                                  <div className="text-lg font-semibold">{formatNumber(segmentStats.reachableUsers)}</div>
                                </div>
                              </div>
                              <div className="mt-3 text-sm text-gray-500">
                                Segment contains all users
                              </div>
                            </div>}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                      <button
                        type="button"
                        className="rounded-md bg-[var(--brand-primary)] px-3 py-2 text-sm font-semibold text-[#fff] shadow-sm hover:bg-[#3599ab]"
                        onClick={handleUpdateJourney}
                      >
                        Update
                      </button>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
          </div>
        </Dialog>
      </Transition.Root>

      <EmailDrawer
  isOpen={isEmailDrawerOpen}
  onClose={handleEmailDrawerClose}
  onSave={handleEmailDrawerSave}
  initialData={selectedEmailNode?.data}
/>
<WhatsappDrawer
  isOpen={isWhatsappDrawerOpen}
  onClose={handleWhatsappDrawerClose}
  onSave={handleWhatsappDrawerSave}
  initialData={selectedWhatsappNode?.data}
/>
      <DateDrawer
        isOpen={isDateDrawerOpen}
        onClose={() => {
          setIsDateDrawerOpen(false);
          setSelectedDateNode(null);
        }}
        onSave={(dateTime) => {
          handleDateSelection(dateTime);
        }}
        initialDate={selectedDateNode?.data?.selectedDate}
      />

      <WaitTimeDrawer
        isOpen={isWaitTimeDrawerOpen}
        onClose={() => {
          setIsWaitTimeDrawerOpen(false);
          setSelectedWaitTimeNode(null);
        }}
        onSave={handleWaitTimeSelection}
        initialData={selectedWaitTimeNode?.data?.waitTimeConfig}
      />

      <WaitTimeSlotsDrawer
        isOpen={isWaitTimeSlotsDrawerOpen}
        onClose={() => {
          setIsWaitTimeSlotsDrawerOpen(false);
          setSelectedWaitTimeSlotsNode(null);
        }}
        onSave={handleWaitTimeSlotsSelection}
        initialData={selectedWaitTimeSlotsNode?.data?.timeSlotConfig}
      />
    </>
  );
};

export default JourneyBuilder;